import { fetchWithActivity } from '../utils/fetchWithActivity';

const RAW_BASE = process.env.REACT_APP_API_BASE_URL || '';
const API_BASE_URL = RAW_BASE.replace(/\/+$/, ''); // strip trailing slashes

// Build a URL that always ends up at `<host>/api/<path>` regardless of whether
// the env var already has `/api` baked in, or is set to the bare host, or is
// empty (relative). Mobile pages call paths like `/customers` and rely on this.
function buildUrl(path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE_URL) {
    // Same-origin: just ensure /api prefix
    return clean.startsWith('/api/') || clean === '/api' ? clean : `/api${clean}`;
  }
  if (/\/api$/.test(API_BASE_URL)) {
    // Base already ends with /api — don't double it.
    return clean.startsWith('/api/') ? `${API_BASE_URL}${clean.slice(4)}` : `${API_BASE_URL}${clean}`;
  }
  return clean.startsWith('/api/') ? `${API_BASE_URL}${clean}` : `${API_BASE_URL}/api${clean}`;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = localStorage.getItem('riskninja-token');
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(extra || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = path.startsWith('http') ? path : buildUrl(path);
  const res = await fetchWithActivity(url, { headers: authHeaders() });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`GET ${path} failed: ${res.status} ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export async function apiPostJson<T>(path: string, body: any): Promise<T> {
  const url = path.startsWith('http') ? path : buildUrl(path);
  const res = await fetchWithActivity(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`POST ${path} failed: ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export async function apiPostFormData<T>(path: string, form: FormData, onProgress?: (pct: number) => void): Promise<T> {
  const url = path.startsWith('http') ? path : buildUrl(path);
  // Use XHR for upload progress; fetch doesn't expose it. Because this
  // bypasses the global fetch interceptor, we have to dispatch
  // `api:unauthorized` ourselves so AuthContext can refresh-or-logout.
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    const token = localStorage.getItem('riskninja-token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText) as T); }
        catch { reject(new Error('Invalid JSON response')); }
      } else {
        if (xhr.status === 401) {
          window.dispatchEvent(new CustomEvent('api:unauthorized', { detail: { url, status: 401 } }));
          reject(new Error('Your session expired. Please sign in again.'));
          return;
        }
        reject(new Error(`POST ${path} failed: ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
      }
    };
    xhr.onerror = () => reject(new Error(`Network error on POST ${path}`));
    xhr.send(form);
  });
}

export { API_BASE_URL };
